import React, { useState, useEffect, useCallback } from 'react';
import { getGooglePickerConfig, importGoogleDocs, GooglePickerConfig, GoogleImportResult } from '../../../api/documentApi';
import './ImportGoogleDocsModal.css';

interface ImportGoogleDocsModalProps {
    /** Passed in from the editor, which is where the language hook lives */
    t: any;
    onClose: () => void;
    // Called after a successful import so the parent can refresh its list
    onImported: () => void;
}

// drive.file lets the app touch only files the user explicitly picks in the
// Google Picker — a non-sensitive scope, so no app verification / CASA.
const DRIVE_FILE_SCOPE = 'https://www.googleapis.com/auth/drive.file';
const GIS_SRC = 'https://accounts.google.com/gsi/client';
const GAPI_SRC = 'https://apis.google.com/js/api.js';

// Load a script tag once, shared across mounts.
const scriptPromises: Record<string, Promise<void> | undefined> = {};
const loadScript = (src: string): Promise<void> => {
    const existing = scriptPromises[src];
    if (existing) return existing;
    const promise = new Promise<void>((resolve, reject) => {
        const s = document.createElement('script');
        s.src = src;
        s.async = true;
        s.defer = true;
        s.onload = () => resolve();
        s.onerror = () => reject(new Error(`Failed to load ${src}`));
        document.head.appendChild(s);
    });
    scriptPromises[src] = promise;
    return promise;
};

type Phase = 'loading' | 'ready' | 'importing' | 'done' | 'unconfigured';

// Google hands back a short-lived token with no refresh token (that is the
// price of staying on the non-sensitive drive.file scope). Holding it in
// memory for its lifetime means back-to-back imports reuse the same grant
// instead of bouncing through Google every time. Deliberately not persisted
// to storage — an access token in localStorage outlives the tab that earned
// it. A reload therefore starts over, by design.
let cachedToken: { token: string; expiresAt: number } | null = null;
const TOKEN_SAFETY_MARGIN_MS = 60_000;

const cachedAccessToken = (): string | null => {
    if (!cachedToken) return null;
    if (cachedToken.expiresAt - Date.now() < TOKEN_SAFETY_MARGIN_MS) {
        cachedToken = null;
        return null;
    }
    return cachedToken.token;
};

export const ImportGoogleDocsModal: React.FC<ImportGoogleDocsModalProps> = ({
    t,
    onClose,
    onImported,
}) => {
    const [phase, setPhase] = useState<Phase>('loading');
    const [config, setConfig] = useState<GooglePickerConfig | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<GoogleImportResult | null>(null);

    // Load config + the Google scripts up front
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const cfg = await getGooglePickerConfig();
                if (cancelled) return;
                if (!cfg.client_id || !cfg.api_key || !cfg.app_id) {
                    setPhase('unconfigured');
                    return;
                }
                setConfig(cfg);
                await Promise.all([loadScript(GIS_SRC), loadScript(GAPI_SRC)]);
                if (cancelled) return;
                await new Promise<void>((resolve) => (window as any).gapi.load('picker', resolve));
                if (cancelled) return;
                setPhase('ready');
            } catch (e: any) {
                if (!cancelled) {
                    setError(e?.message || t.importDocs.pickerLoadFailed);
                    setPhase('ready');
                }
            }
        })();
        return () => { cancelled = true; };
    }, [t]);

    const runImport = useCallback(async (
        accessToken: string,
        docIds: string[],
        docNames: Record<string, string>,
    ) => {
        setPhase('importing');
        setError(null);
        try {
            const res = await importGoogleDocs(docIds, accessToken, docNames);
            // A rejected token has to be dropped or every retry reuses it
            if (res.errors.some((e) => /expired/i.test(e.error))) cachedToken = null;
            setResult(res);
            setPhase('done');
            if (res.success_count > 0) onImported();
        } catch (e: any) {
            setError(e?.response?.data?.message || e?.response?.data?.error || t.importDocs.importFailed);
            setPhase('ready');
        }
    }, [onImported, t]);

    const openPicker = useCallback((accessToken: string) => {
        const google = (window as any).google;
        // Barest Google-Docs list: no folders, no owner/parent/mimeType
        // filters (DOCUMENTS is already Google Docs only). This is the
        // config Google's own Picker samples use; extra filters were
        // suppressing the file list.
        const view = new google.picker.DocsView(google.picker.ViewId.DOCUMENTS)
            .setMode(google.picker.DocsViewMode.LIST);

        const picker = new google.picker.PickerBuilder()
            .enableFeature(google.picker.Feature.MULTISELECT_ENABLED)
            .setAppId(config!.app_id)
            .setOAuthToken(accessToken)
            .setDeveloperKey(config!.api_key)
            .addView(view)
            .setTitle(t.importDocs.pickerTitle)
            .setCallback((data: any) => {
                if (data.action === google.picker.Action.PICKED) {
                    const picked = data.docs || [];
                    const docIds = picked.map((d: any) => d.id);
                    const docNames: Record<string, string> = {};
                    picked.forEach((d: any) => { if (d.id && d.name) docNames[d.id] = d.name; });
                    if (docIds.length > 0) runImport(accessToken, docIds, docNames);
                }
            })
            .build();
        picker.setVisible(true);
    }, [config, runImport, t]);

    const handleChoose = useCallback(() => {
        if (!config) return;
        setError(null);

        // Still holding a live grant: straight to the file browser
        const existing = cachedAccessToken();
        if (existing) {
            openPicker(existing);
            return;
        }

        const google = (window as any).google;
        const tokenClient = google.accounts.oauth2.initTokenClient({
            client_id: config.client_id,
            scope: DRIVE_FILE_SCOPE,
            // Naming the account lets Google skip the chooser when that user
            // is already signed in and has granted the scope before
            ...(config.email ? { hint: config.email } : {}),
            callback: (resp: any) => {
                if (resp.error) {
                    setError(t.importDocs.authCancelled);
                    return;
                }
                cachedToken = {
                    token: resp.access_token,
                    // expires_in is seconds; Google's default is an hour
                    expiresAt: Date.now() + (Number(resp.expires_in) || 3600) * 1000,
                };
                openPicker(resp.access_token);
            },
        });
        // '' = don't force the consent screen when the grant already exists
        tokenClient.requestAccessToken({ prompt: '' });
    }, [config, openPicker, t]);

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="import-modal google-docs-modal" onClick={(e) => e.stopPropagation()}>
                <div className="import-modal-header">
                    <h2>{t.importDocs.googleTitle}</h2>
                    <button className="close-btn" onClick={onClose}>✕</button>
                </div>

                <div className="import-modal-content">
                    {phase === 'loading' && (
                        <div className="auth-screen"><p>{t.importDocs.loadingPicker}</p></div>
                    )}

                    {phase === 'unconfigured' && (
                        <div className="auth-screen">
                            <h3>{t.importDocs.unconfiguredTitle}</h3>
                            <p className="auth-description">
                                {t.importDocs.unconfiguredDesc}
                            </p>
                        </div>
                    )}

                    {(phase === 'ready' || phase === 'importing') && (
                        <div className="auth-screen">
                            <div className="google-icon">
                                <svg width="64" height="64" viewBox="0 0 48 48">
                                    <path fill="#4285F4" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
                                    <path fill="#34A853" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
                                    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
                                    <path fill="#EA4335" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
                                </svg>
                            </div>
                            <h3>{t.importDocs.googleHeading}</h3>
                            <p className="auth-description">
                                {t.importDocs.googleDesc}
                            </p>
                            {error && (
                                <div style={{ background: '#fee', color: '#c00', padding: '0.75rem', borderRadius: '8px', marginTop: '1rem' }}>
                                    {error}
                                </div>
                            )}
                            <button
                                className="google-auth-btn"
                                onClick={handleChoose}
                                disabled={phase === 'importing'}
                            >
                                {phase === 'importing'
                                    ? t.importDocs.importingEllipsis
                                    : t.importDocs.chooseFromDrive}
                            </button>
                        </div>
                    )}

                    {phase === 'done' && result && (
                        <div className="auth-screen">
                            <h3>{t.importDocs.complete}</h3>
                            <p className="auth-description">
                                {(result.total === 1
                                    ? t.importDocs.importedOne
                                    : t.importDocs.importedMany)
                                    .replace('{count}', String(result.success_count))
                                    .replace('{total}', String(result.total))}
                            </p>
                            {result.imported.length > 0 && (
                                <ul className="import-result-list">
                                    {result.imported.map((d) => (
                                        <li key={d.id}>📄 {d.name}</li>
                                    ))}
                                </ul>
                            )}
                            {!!result.images_skipped && (
                                <p className="auth-description">
                                    {(result.images_skipped === 1
                                        ? t.importDocs.imagesSkippedOne
                                        : t.importDocs.imagesSkippedMany)
                                        .replace('{count}', String(result.images_skipped))}
                                </p>
                            )}
                            {result.error_count > 0 && (
                                <div style={{ background: '#fee', color: '#c00', padding: '0.75rem', borderRadius: '8px', marginTop: '1rem', textAlign: 'left' }}>
                                    <strong>
                                        {(result.error_count === 1
                                            ? t.importDocs.failedOne
                                            : t.importDocs.failedMany)
                                            .replace('{count}', String(result.error_count))}
                                    </strong>
                                    {/* One line per distinct reason — repeating the same
                                        Google error for every file helps nobody */}
                                    <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.1rem' }}>
                                        {Array.from(new Set(result.errors.map((e) => e.error))).map((message) => (
                                            <li key={message}>{message}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="import-modal-footer">
                    {phase === 'done' ? (
                        <button className="modal-btn primary" onClick={onClose}>{t.importDocs.done}</button>
                    ) : (
                        <button className="modal-btn secondary" onClick={onClose}>{t.common.cancel}</button>
                    )}
                </div>
            </div>
        </div>
    );
};
